import { ChevronRight, type LucideIcon } from "lucide-react"
import { Link } from "@tanstack/react-router"
import { useState, type ReactNode } from "react"

import { AnimatedCollapse } from "@/components/shared/animated-collapse"
import { collapseChevronTransitionClass } from "@/lib/animation-variants"
import { cn } from "@/lib/utils"
import { useSectionCollapsed, sidebarActions } from "@/stores/sidebar.store"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"

/**
 * Rounded nav rows. TanStack Router marks the link for the current location
 * with `data-status="active"`, which fills the row like a selected tab.
 */
const navItemClassName =
  "rounded-lg data-[status=active]:bg-sidebar-accent data-[status=active]:font-medium data-[status=active]:text-sidebar-accent-foreground"

interface NavItem {
  title: string
  url: string
  icon?: LucideIcon
  isActive?: boolean
  badge?: ReactNode
  items?: {
    title: string
    url: string
    icon?: LucideIcon
  }[]
}

function NavCollapsibleItem({ item }: { item: NavItem }) {
  const [open, setOpen] = useState(item.isActive ?? false)

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={item.title}
        className="rounded-lg"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {item.icon && <item.icon />}
        <span>{item.title}</span>
        <ChevronRight className={cn("ml-auto", collapseChevronTransitionClass, open && "rotate-90")} />
      </SidebarMenuButton>
      <AnimatedCollapse open={open}>
        <SidebarMenuSub>
          {item.items?.map((subItem) => (
            <SidebarMenuSubItem key={subItem.title}>
              <SidebarMenuSubButton asChild className={navItemClassName}>
                <Link to={subItem.url}>
                  {subItem.icon && <subItem.icon />}
                  <span>{subItem.title}</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      </AnimatedCollapse>
    </SidebarMenuItem>
  )
}

export function NavMain({
  items,
  label,
  collapsible = false,
}: {
  items: NavItem[]
  label?: string
  collapsible?: boolean
}) {
  const menuContent = (
    <SidebarMenu>
        {items.map((item) => (
          item.items ? (
            <NavCollapsibleItem key={item.title} item={item} />
          ) : (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton asChild tooltip={item.title} className={navItemClassName}>
                <Link to={item.url}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  {item.badge}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        ))}
    </SidebarMenu>
  )

  const sectionKey = label ?? ''
  const isCollapsed = useSectionCollapsed(sectionKey)

  if (collapsible && label) {
    return (
      <SidebarGroup>
        <SidebarGroupLabel asChild>
          <button
            type="button"
            aria-expanded={!isCollapsed}
            onClick={() => sidebarActions.setSectionCollapsed(sectionKey, !isCollapsed)}
            className="w-full cursor-pointer text-left hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-lg transition-colors"
          >
            <span className="flex-1">{label}</span>
            <ChevronRight className={cn("ml-auto h-4 w-4", collapseChevronTransitionClass, !isCollapsed && "rotate-90")} />
          </button>
        </SidebarGroupLabel>
        <AnimatedCollapse open={!isCollapsed}>
          {menuContent}
        </AnimatedCollapse>
      </SidebarGroup>
    )
  }

  return (
    <SidebarGroup>
      {label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
      {menuContent}
    </SidebarGroup>
  )
}
